import type {
  BlueprintBacklogInput,
  BlueprintMetaInput,
  BlueprintRuleInput,
  BlueprintStepInput,
  BusinessRuleInput,
  CreateBusinessRuleInput,
} from '@prepzio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { adminApi } from '../lib/api';
import { adminKeys } from '../lib/query';

/**
 * PRE-190: server-state hooks for the product document.
 *
 * Business rules and the blueprint used to be compiled into this bundle — a
 * markdown `?raw` import and a TypeScript module — so the panel could only ever
 * display them and a product-owner edit meant a pull request. They now come from
 * D1 over the API, and these hooks are how the three document screens read and
 * write them.
 *
 * Every mutation invalidates `rtm` alongside its own key: the matrix is derived
 * from this content on the server, so editing a step's trace anchors or a rule's
 * id changes the matrix too. Leaving the matrix cached after a document edit
 * would show an operator a verdict computed against content that no longer
 * exists — the exact drift this move was meant to end.
 */

function useDocumentMutation<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  key: readonly unknown[],
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['admin', 'rtm'] });
    },
  });
}

// ── Business rules ────────────────────────────────────────────────────────────

export function useBusinessRules() {
  return useQuery({
    queryKey: adminKeys.businessRules(),
    queryFn: () => adminApi.getBusinessRules(),
  });
}

export function useCreateBusinessRule() {
  return useDocumentMutation(
    (input: CreateBusinessRuleInput) => adminApi.createBusinessRule(input),
    adminKeys.businessRules(),
  );
}

export function useUpdateBusinessRule() {
  return useDocumentMutation(
    ({ id, input }: { id: string; input: BusinessRuleInput }) =>
      adminApi.updateBusinessRule(id, input),
    adminKeys.businessRules(),
  );
}

/**
 * PRE-126/102: record a review of a rule, or take it back.
 *
 * The only write the panel makes on a rule. The sentence travels with the
 * deploy; what a person adds here is a verdict on it — `confirmed`, or
 * `rejected` with the reason that makes the rejection readable a month later.
 */
export function useReviewBusinessRule() {
  return useDocumentMutation(
    ({
      id,
      reviewed,
      verdict,
      note,
    }: {
      id: string;
      reviewed: boolean;
      verdict?: 'confirmed' | 'rejected';
      note?: string;
    }) => adminApi.reviewBusinessRule(id, reviewed, verdict, note),
    adminKeys.businessRules(),
  );
}

// ── Blueprint ─────────────────────────────────────────────────────────────────

export function useBlueprint() {
  return useQuery({ queryKey: adminKeys.blueprint(), queryFn: () => adminApi.getBlueprint() });
}

export function useUpdateBlueprintMeta() {
  return useDocumentMutation(
    (input: BlueprintMetaInput) => adminApi.updateBlueprintMeta(input),
    adminKeys.blueprint(),
  );
}

export function useUpdateBlueprintStep() {
  return useDocumentMutation(
    ({ id, input }: { id: string; input: BlueprintStepInput }) =>
      adminApi.updateBlueprintStep(id, input),
    adminKeys.blueprint(),
  );
}

/**
 * PRE-81: rules gained anchors, so they gained an editor. Which line enforces an
 * invariant is a product judgement — the review can read code, but only a person
 * knows what was promised — so anchors are typed here rather than derived.
 */
export function useUpdateBlueprintRule() {
  return useDocumentMutation(
    ({ id, input }: { id: string; input: BlueprintRuleInput }) =>
      adminApi.updateBlueprintRule(id, input),
    adminKeys.blueprint(),
  );
}

export function useUpdateBlueprintBacklog() {
  return useDocumentMutation(
    ({ id, input }: { id: string; input: BlueprintBacklogInput }) =>
      adminApi.updateBlueprintBacklog(id, input),
    adminKeys.blueprint(),
  );
}
